import { useEffect } from 'react';
import { useSession } from '../../contexts/SessionContext';
import { useCalendar } from '../../contexts/CalendarContext';
import { getPossessionFields, fieldStateKey } from '../../utils/dataFields';
import PossessionFields from './PossessionFields';

/**
 * Shared possession section for the non-vehicle types (animal, real_estate):
 * a generic config-driven field set plus an existing-possession picker for a
 * verified customer. Parameterized by which customer-record array to read
 * (`possessionsKey`) and how to label an existing option (`optionLabel`).
 */
const GenericPossessionInfo = ({ customerRecords, possessionsKey, optionLabel }) => {
  const {
    personalInfo,
    setPersonalInfo,
    selectedCustomerId,
    selectedVehicleId,
    setSelectedVehicleId,
    setVehicleCustomerRefId,
    setVehiclePossessionId,
  } = useSession();
  const { fields } = useCalendar();

  const possessionFields = getPossessionFields(fields);
  const selectedCustomer = customerRecords.find((c) => c.id === selectedCustomerId);
  const existing = (selectedCustomerId && selectedCustomerId !== 'new' && selectedCustomer?.[possessionsKey]) || [];

  const prefillFrom = (poss) => {
    setVehicleCustomerRefId(poss?.customer_ref_id || null);
    setVehiclePossessionId(poss?.possession_id || null);
    setPersonalInfo((prev) => ({
      ...prev,
      ...possessionFields.reduce((acc, f) => {
        const key = fieldStateKey(f);
        acc[key] = poss?.[key] ?? '';
        return acc;
      }, {}),
    }));
  };

  const clearFields = () => {
    setVehicleCustomerRefId(null);
    setVehiclePossessionId(null);
    setPersonalInfo((prev) => ({
      ...prev,
      ...possessionFields.reduce((acc, f) => {
        acc[fieldStateKey(f)] = '';
        return acc;
      }, {}),
    }));
  };

  const handleSelect = (e) => {
    const id = e.target.value;
    setSelectedVehicleId(id);
    if (id === 'new' || !id) {
      clearFields();
    } else {
      const poss = existing.find((p) => p.id === id);
      if (poss) prefillFrom(poss);
    }
  };

  // When a verified customer is selected, auto-select their first possession.
  useEffect(() => {
    if (!selectedCustomerId || selectedCustomerId === 'new' || selectedVehicleId) return;
    const first = existing[0];
    if (first) {
      setSelectedVehicleId(first.id);
      prefillFrom(first);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCustomerId, customerRecords]);

  const showFields = !selectedVehicleId || selectedVehicleId === 'new' || !existing.length;

  return (
    <>
      {existing.length > 0 && (
        <div className="form-group">
          <label htmlFor="possessionSelect">Select existing</label>
          <select
            id="possessionSelect"
            value={selectedVehicleId || ''}
            onChange={handleSelect}
            style={{ width: '100%', padding: '10px', marginBottom: '20px' }}
          >
            <option value="">-- Select an option --</option>
            {existing.map((poss) => (
              <option key={poss.id} value={poss.id}>
                {optionLabel(poss)}
              </option>
            ))}
            <option value="new">Add New</option>
          </select>
        </div>
      )}

      {showFields && (
        <div className="form-row" style={{ flexWrap: 'wrap' }}>
          <PossessionFields fields={possessionFields} personalInfo={personalInfo} setPersonalInfo={setPersonalInfo} />
        </div>
      )}
    </>
  );
};

export default GenericPossessionInfo;
